import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import { X, Loader2, Users, EyeOff, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * StreakMilestoneCelebration
 * --------------------------
 * Full-screen "🔥 N-DAY STREAK" moment fired when a user's consecutive
 * training days hit 7, 30 or 100. Triggered by the backend via the
 * `streak:milestone` WebSocket event right after /sessions/complete
 * (plus a web-push for backgrounded tabs). Design parity with
 * GoalHitCelebration: bouncing icon, share-card canvas, squad-post strip
 * with a hide/undo action.
 */
const APP_URL = "https://gymwithgrizz.app";

const MILESTONE_LABELS = {
  7: "ONE FULL WEEK",
  30: "A WHOLE MONTH",
  100: "TRIPLE DIGITS",
};

export default function StreakMilestoneCelebration() {
  const { subscribe } = useWebSocket();
  const { user } = useAuth();
  const [hit, setHit] = useState(null); // { streak_days, post_id }
  const [postHidden, setPostHidden] = useState(false);
  const [hidingPost, setHidingPost] = useState(false);
  const canvasRef = useRef(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const unsub = subscribe("streak:milestone", (msg) => {
      if (!user || msg.user_id !== user.user_id) return;
      setHit({ streak_days: Number(msg.streak_days), post_id: msg.post_id || null });
      setPostHidden(false);
    });
    return () => unsub && unsub();
    // eslint-disable-next-line
  }, [subscribe, user?.user_id]);

  // Share card.
  useEffect(() => {
    if (!hit || !canvasRef.current) { setImageUrl(null); return; }
    setGenerating(true);
    const c = canvasRef.current;
    c.width = 1080; c.height = 1920;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, 1080, 1920);
    for (let i = -1920; i < 1920; i += 240) {
      ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + 1920, 1920);
      ctx.lineWidth = 60; ctx.strokeStyle = "rgba(59, 130, 246, 0.06)"; ctx.stroke();
    }
    ctx.fillStyle = "#3b82f6";
    ctx.fillRect(0, 0, 1080, 14); ctx.fillRect(0, 1906, 1080, 14);
    ctx.font = "bold 360px sans-serif"; ctx.textAlign = "center";
    ctx.fillText("🔥", 540, 480);
    ctx.fillStyle = "#3b82f6"; ctx.font = "bold 220px sans-serif";
    ctx.fillText(`${hit.streak_days}`, 540, 760);
    ctx.fillStyle = "#ffffff"; ctx.font = "bold 110px sans-serif";
    ctx.fillText("DAY STREAK", 540, 900);
    ctx.fillStyle = "#a3a3a3"; ctx.font = "54px sans-serif";
    ctx.fillText(MILESTONE_LABELS[hit.streak_days] || "NO DAYS OFF", 540, 1000);
    ctx.fillStyle = "rgba(255,255,255,0.04)";
    ctx.fillRect(80, 1120, 920, 260);
    ctx.strokeStyle = "#262626"; ctx.lineWidth = 4;
    ctx.strokeRect(80, 1120, 920, 260);
    ctx.fillStyle = "#60a5fa"; ctx.font = "bold 72px sans-serif";
    ctx.fillText(`${hit.streak_days} days straight of training`, 540, 1230);
    ctx.fillStyle = "#a3a3a3"; ctx.font = "44px sans-serif";
    ctx.fillText("NO. DAYS. OFF.", 540, 1320);
    ctx.fillStyle = "#ffffff"; ctx.font = "bold 80px sans-serif";
    ctx.fillText("GYM with GRIZZ", 540, 1620);
    ctx.fillStyle = "#3b82f6"; ctx.font = "44px sans-serif";
    ctx.fillText(APP_URL.replace(/^https?:\/\//, ""), 540, 1700);
    ctx.fillStyle = "#737373"; ctx.font = "36px sans-serif";
    ctx.fillText("#gymwithgrizz", 540, 1780);
    c.toBlob((blob) => {
      if (blob) setImageUrl(URL.createObjectURL(blob));
      setGenerating(false);
    }, "image/png", 0.92);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hit]);

  if (!hit) return null;

  const caption = `${hit.streak_days}-day training streak. No days off. 🔥 #gymwithgrizz`;

  const close = () => {
    setHit(null);
    if (imageUrl) try { URL.revokeObjectURL(imageUrl); } catch {}
    setImageUrl(null);
  };

  const shareAll = async () => {
    if (imageUrl) {
      const a = document.createElement("a");
      a.href = imageUrl; a.download = `gymwithgrizz-${hit.streak_days}-day-streak.png`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    }
    try { await navigator.clipboard.writeText(caption); } catch {}
    toast.success("Image saved + caption copied — share it anywhere", { duration: 5000 });
  };

  const hidePost = async () => {
    if (!hit?.post_id || postHidden) return;
    setHidingPost(true);
    try {
      await api.delete(`/feed/${hit.post_id}`);
      setPostHidden(true);
      toast.success("Post removed from your Squad");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't hide post");
    } finally { setHidingPost(false); }
  };

  return (
    <div data-testid="streak-milestone-overlay" className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4 overflow-y-auto">
      <div className="max-w-md w-full text-center">
        <button data-testid="streak-milestone-close" onClick={close} className="absolute top-4 right-4 text-neutral-500 hover:text-white p-2" title="Close">
          <X className="w-6 h-6" />
        </button>

        <div className="animate-bounce text-7xl mb-3">🔥</div>
        <div data-testid="streak-milestone-headline" className="font-display text-5xl text-blue-500 uppercase tracking-tight animate-pulse mb-1">
          {hit.streak_days}-Day Streak!
        </div>
        <div className="text-sm uppercase tracking-widest text-neutral-400 mb-6">
          {MILESTONE_LABELS[hit.streak_days] || "No days off"} — <span className="text-white">{hit.streak_days} days straight</span>
        </div>

        <canvas ref={canvasRef} className="hidden" />
        {generating ? (
          <div className="text-neutral-500 mb-4 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Generating share card…
          </div>
        ) : imageUrl ? (
          <img src={imageUrl} alt="Streak share card" data-testid="streak-milestone-preview" className="block mx-auto w-44 mb-5 border border-neutral-800" />
        ) : null}

        {hit.post_id && (
          <div data-testid="streak-milestone-squad-strip" className={`flex items-center justify-between gap-3 border ${postHidden ? "border-neutral-800 bg-neutral-900/40" : "border-blue-500/30 bg-blue-500/5"} p-2.5 mb-3`}>
            <div className="flex items-center gap-2 text-left">
              <Users className={`w-4 h-4 ${postHidden ? "text-neutral-500" : "text-blue-500"}`} />
              <div>
                <div className={`text-[11px] uppercase tracking-widest font-display ${postHidden ? "text-neutral-500" : "text-blue-500"}`}>
                  {postHidden ? "Post hidden" : "Shared to your Squad"}
                </div>
                <div className="text-[10px] text-neutral-500">
                  {postHidden ? "Buddies & trainers won't see it" : "Buddies & trainers see it live"}
                </div>
              </div>
            </div>
            {!postHidden && (
              <button data-testid="streak-milestone-hide-post" onClick={hidePost} disabled={hidingPost}
                className="text-[10px] uppercase tracking-widest font-display text-neutral-400 hover:text-white flex items-center gap-1 px-2 py-1 border border-neutral-700 hover:border-white">
                {hidingPost ? <Loader2 className="w-3 h-3 animate-spin" /> : <EyeOff className="w-3 h-3" />}
                Hide
              </button>
            )}
          </div>
        )}

        <button data-testid="streak-milestone-share" onClick={shareAll}
          className="w-full h-11 bg-blue-500 hover:bg-blue-600 text-white font-display uppercase tracking-wider flex items-center justify-center gap-2 mb-3">
          <Download className="w-4 h-4" /> Save & Share
        </button>
        <button data-testid="streak-milestone-dismiss" onClick={close}
          className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white">
          Keep Training
        </button>
      </div>
    </div>
  );
}
